import React, { useCallback, memo, useState } from 'react';
import { SelectOption } from '@leasequery/lq.react-component-library/dist/models';
import {
  ClientCustomCalendarModel,
  ReportCurrency,
  CustomCalendarPeriods,
  ReportType,
  ClientSetting,
} from '../../../../models';
import * as Styled from '../styled';
import { INPUT_MASK } from '../../../../constants';
import { validate } from '../../../../util/validation';
import { ReportTypeSetting } from '../../../../models/reportTypeSetting';
import ReportSettingPIT from '../ReportSettingPIT';
import { ReportSettingReportDateInput } from '../ReportSettingReportDateInput';
import { getStartMonthDate } from '../../../../util';
import { getCustomCalendarPeriodEndDate } from '../../../../util/customCalendarPeriods';
import { useAuth } from '../../../../context';
import { Icon } from '../../../../components';

interface ReportSettingROUAssetsProps {
  onChange: (updatedValue: ReportSettingROUAssetsState) => void;
  reportSettingState: ReportSettingROUAssetsState;
  reportTypeSetting: ReportTypeSetting;
  customCalendarSettings?: ClientCustomCalendarModel;
  isAllowPIT: boolean;
}

export interface ReportSettingROUAssetsState {
  selectedCurrency: SelectOption<ReportCurrency>;
  reportEndDate: string;
  isValid: boolean;
}

const validateReportEndDate = (
  value: string,
  customCalendarSettings: ClientCustomCalendarModel,
  clientSetting: ClientSetting,
): string => {
  const { isCustomCalendarEnabled, numberOfPeriods } = customCalendarSettings;
  let validationResult = '';

  // validate date format
  let reportEndDate: Date;
  if (isCustomCalendarEnabled) {
    validationResult = validate(value, CustomCalendarPeriods[numberOfPeriods]);
    if (!!validationResult) return validationResult;
    reportEndDate = getCustomCalendarPeriodEndDate(
      value,
      customCalendarSettings,
    );
  } else {
    validationResult = validate(value, { shortDate: 'Invalid date' });
    if (!!validationResult) return validationResult;
    reportEndDate = getStartMonthDate(value);
  }

  // validate transition date
  const ifrsTransitionDate = getStartMonthDate(
    `${clientSetting.ifrsTransitionMonth}/${clientSetting.ifrsTransitionYear}`,
  );
  if (reportEndDate < ifrsTransitionDate) {
    return 'The Report End Date cannot be before your Transition Date';
  }

  return '';
};

function ReportDateSettingROUAssets({
  onChange,
  reportSettingState,
  customCalendarSettings,
  reportTypeSetting,
  isAllowPIT,
}: ReportSettingROUAssetsProps): JSX.Element {
  const { clientSetting } = useAuth();
  const [reportDateError, updateReportDateError] = useState('');
  const { isCustomCalendarEnabled } = customCalendarSettings;
  const placeholder = isCustomCalendarEnabled
    ? INPUT_MASK.CUSTOM_CALENDAR_PLACEHOLDER
    : INPUT_MASK.SHORT_DATE_PLACEHOLDER;

  const mask = isCustomCalendarEnabled
    ? INPUT_MASK.CUSTOM_CALENDAR
    : INPUT_MASK.SHORT_DATE;

  const dateInputHandler = useCallback(
    (value: string): void => {
      const validationResult = validateReportEndDate(
        value,
        customCalendarSettings,
        clientSetting,
      );

      updateReportDateError(validationResult);
      onChange({
        ...reportSettingState,
        isValid: !validationResult,
        reportEndDate: value,
      });
    },
    [customCalendarSettings, clientSetting, onChange, reportSettingState],
  );

  return (
    <>
      <Styled.ReportDateRow>
        <div>
          <Icon name="calendar" />
        </div>
        <ReportSettingReportDateInput
          required={true}
          name="reportDate"
          className="shortDate"
          label="Report End Date"
          placeholder={placeholder}
          mask={mask}
          value={reportSettingState.reportEndDate}
          error={reportDateError}
          onChange={useCallback(
            (event): void => {
              dateInputHandler(event.target.value.trim());
            },
            [dateInputHandler],
          )}
        />
      </Styled.ReportDateRow>
      <Styled.Spacer />
      <Styled.Line />
      <ReportSettingPIT
        reportType={ReportType.rouAssets}
        isAllowPIT={isAllowPIT}
        reportSettingState={reportSettingState}
        reportTypeSetting={reportTypeSetting}
        customCalendarSettings={customCalendarSettings}
        onChange={useCallback(
          (state) => onChange(state as ReportSettingROUAssetsState),
          [onChange],
        )}
      />
    </>
  );
}

export default memo(ReportDateSettingROUAssets);
